import { ChevronDown } from 'lucide-react';
import { useState, type ReactNode } from 'react';

export type AccordionBadge = 'Complete' | 'Required' | 'Optional';

type SectionProps = {
  id: string;
  title: string;
  badge?: AccordionBadge;
  defaultOpen?: boolean;
  children: ReactNode;
};

export function RoadDetailsAccordionSection({
  title,
  badge,
  defaultOpen = false,
  children,
}: SectionProps) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-xl border border-gray-200 dark:border-neutral-600 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2.5 bg-gray-50/80 dark:bg-neutral-800/50 text-left"
      >
        <span className="text-sm font-semibold text-gray-800 dark:text-neutral-200">{title}</span>
        <span className="flex items-center gap-2 shrink-0">
          {badge ? (
            <span
              className={`text-[10px] font-medium uppercase tracking-wide px-1.5 py-0.5 rounded ${
                badge === 'Complete'
                  ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-200'
                  : badge === 'Required'
                    ? 'bg-amber-100 text-amber-900 dark:bg-amber-950/40 dark:text-amber-200'
                    : 'bg-gray-200 text-gray-600 dark:bg-neutral-700 dark:text-neutral-300'
              }`}
            >
              {badge}
            </span>
          ) : null}
          <ChevronDown
            size={18}
            className={`text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`}
          />
        </span>
      </button>
      {open ? <div className="p-3 space-y-3 border-t border-gray-200 dark:border-neutral-600">{children}</div> : null}
    </div>
  );
}

type GeometryStatusProps = {
  layoutSubtitle: string;
  rows: { label: string; statusLabel: string }[];
  onEditGeometry?: () => void;
};

export function RoadGeometryStatusBar({ layoutSubtitle, rows, onEditGeometry }: GeometryStatusProps) {
  return (
    <div className="rounded-xl border border-sky-200 dark:border-sky-900/50 bg-sky-50/60 dark:bg-sky-950/20 p-3 space-y-2">
      <p className="text-sm font-semibold text-gray-900 dark:text-neutral-100">{layoutSubtitle}</p>
      <p className="text-xs font-medium text-gray-700 dark:text-neutral-300">Geometry status</p>
      <ul className="text-xs space-y-1 text-gray-700 dark:text-neutral-300">
        {rows.map((r) => (
          <li key={r.label} className="flex justify-between gap-2">
            <span>{r.label}</span>
            <span className="font-medium">{r.statusLabel}</span>
          </li>
        ))}
      </ul>
      {onEditGeometry ? (
        <button
          type="button"
          onClick={onEditGeometry}
          className="text-xs font-semibold text-primary-600 dark:text-primary-400 hover:underline"
        >
          Edit geometry
        </button>
      ) : null}
    </div>
  );
}
